import { useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentLang } from "@/hooks/useLanguage";

export default function Header() {
  const [searchValue, setSearchValue] = useState("");
  const [fontSize, setFontSize] = useState(16);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const lang = useCurrentLang();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      const prefix = lang === "fr" ? "/fr" : "";
      navigate(`${prefix}/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue("");
    }
  };

  const adjustFont = (delta: number) => {
    const next = Math.min(20, Math.max(13, fontSize + delta));
    setFontSize(next);
    document.documentElement.style.fontSize = `${next}px`;
  };

  const isFrench = lang === "fr";

  const handleLangSwitch = useCallback((targetLang: "en" | "fr") => {
    const isCurrentlyFrench = lang === "fr";
    const targetIsFrench = targetLang === "fr";

    if (isCurrentlyFrench === targetIsFrench) return;

    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentHash = location.hash;

    let targetUrl: string;
    if (targetLang === "en") {
      if (currentPath === "/fr") {
        targetUrl = "/" + currentSearch + currentHash;
      } else {
        targetUrl = currentPath.replace(/^\/fr/, "") + currentSearch + currentHash;
      }
    } else {
      if (currentPath === "/") {
        targetUrl = "/fr" + currentSearch + currentHash;
      } else {
        targetUrl = "/fr" + currentPath + currentSearch + currentHash;
      }
    }

    navigate(targetUrl);
  }, [lang, location.pathname, location.search, location.hash, navigate]);

  return (
    <header className="bg-[#0d1f4c] w-full border-b border-white/10">
      <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to={isFrench ? "/fr" : "/"} className="flex-shrink-0 cursor-pointer">
          <img
            src="/wp-content/uploads/2016/04/Mpf_logo-April2016.png"
            alt="Musicians' Pension Fund of Canada"
            title="MPFCanada – Home"
            className="h-[72px] w-auto object-contain"
          />
        </Link>

        {/* Right controls */}
        <div className="hidden md:flex flex-col items-end gap-2">
          {/* Top row: language + font resize */}
          <div className="flex items-center gap-4">
            {/* Language */}
            <div className="flex items-center gap-2 text-sm">
              <button
                onClick={() => handleLangSwitch("en")}
                className={`hover:text-[#7ec8e3] transition-colors cursor-pointer ${!isFrench ? "text-white font-semibold" : "text-white/70"}`}
              >
                {t("header.english")}
              </button>
              <span className="text-white/30 text-xs">|</span>
              <button
                onClick={() => handleLangSwitch("fr")}
                className={`hover:text-[#7ec8e3] transition-colors cursor-pointer ${isFrench ? "text-white font-semibold" : "text-white/70"}`}
              >
                {t("header.francais")}
              </button>
            </div>

            {/* Font resize */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => adjustFont(-1)}
                className="w-7 h-7 flex items-center justify-center rounded border border-white/20 text-white/70 text-xs hover:bg-white/10 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                title={t("header.decreaseFont")}
                aria-label={t("header.decreaseFont")}
              >
                -A
              </button>
              <button
                onClick={() => adjustFont(1)}
                className="w-7 h-7 flex items-center justify-center rounded border border-white/20 text-white/70 text-sm font-bold hover:bg-white/10 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                title={t("header.increaseFont")}
                aria-label={t("header.increaseFont")}
              >
                +A
              </button>
            </div>
          </div>

          {/* Bottom row: search + member login */}
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="flex items-center">
              <input
                type="search"
                placeholder={t("header.searchPlaceholder")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="text-sm px-3 py-1.5 rounded-l-md border border-white/20 bg-white/10 text-white placeholder-white/40 outline-none focus:bg-white/15 focus:border-[#7ec8e3]/60 transition-all w-40"
                aria-label={t("header.searchAria")}
              />
              <button
                type="submit"
                className="px-3 py-1.5 bg-[#1a8fc1] hover:bg-[#1578a3] text-white rounded-r-md transition-colors cursor-pointer whitespace-nowrap"
                aria-label={t("header.submitSearch")}
              >
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>

            <a
              href="https://www.mpfcanada.ca/trustees/"
              target="_blank"
              rel="nofollow"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#7ec8e3]/60 bg-[#7ec8e3]/8 text-white text-sm font-semibold rounded-md hover:border-[#7ec8e3] hover:bg-[#7ec8e3]/15 focus:outline-none focus:ring-2 focus:ring-[#7ec8e3]/40 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-team-line text-sm"></i>
              {t("header.trustees")}
            </a>

            <a
              href={`/member-ui/login/MPF/Member/${isFrench ? 'fr' : 'en'}`}
              target="_blank"
              rel="nofollow"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#1a8fc1] hover:bg-[#1578a3] text-white text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-line text-sm"></i>
              {t("header.memberLogin")}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}